/**
 * `paywall_handoff` renderer — terminal screen that hands the user off
 * to a paid checkout (Stripe, RevenueCat, or any future provider).
 *
 * This renderer only shows the hero content (headline, subline, bullets).
 * The primary CTA is rendered by the orchestrator's global footer, which
 * calls `handoffRedirect` for terminal steps. The orchestrator also
 * writes `data.handoffUrl` to the `__handoff_url` answer-bag key so that
 * `useHandoffRedirect` can override `redirect_url` on the Clerk
 * token-signin URL.
 *
 * The optional secondary CTA ("Maybe later") fires a `defer` action
 * for analytics; it stays in the renderer since it's step-specific.
 *
 * No `@nextday/funnel-ui` dependency — paywall handoffs are intentionally
 * minimal so any brand/provider can substitute their own copy + branding
 * via JSON without dragging in funnel-specific UI primitives.
 *
 * Step JSON shape (plan §11 NX-9304 — paywall_handoff terminal):
 *
 *   {
 *     "type": "paywall_handoff",
 *     "data": {
 *       "answerKey": "paywall_handoff",
 *       "headline": "Unlock unlimited conversations",
 *       "subline": "Try free for 7 days, cancel anytime.",
 *       "ctaLabel": "Continue to checkout",
 *       "secondaryLabel": "Maybe later",
 *       "handoffUrl": "/checkout/start?source=onboarding",
 *       "bullets": [
 *         "Unlimited messages with your match",
 *         "Voice replies (coming soon)",
 *         "Cancel anytime"
 *       ]
 *     }
 *   }
 */
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

const ACTIONS = {
  defer: "defer",
} as const;

export const paywallHandoffSchema = z
  .object({
    answerKey: z.string().min(1),
    headline: z.string().min(1),
    subline: z.string().optional(),
    ctaLabel: z.string().min(1),
    secondaryLabel: z.string().min(1).optional(),
    /**
     * Opaque URL or token the orchestrator interprets. The renderer
     * forwards it as the `value` of the begin_handoff answer; it does NOT
     * navigate or fetch anything itself.
     */
    handoffUrl: z.string().min(1),
    bullets: z.array(z.string().min(1)).optional(),
  })
  .strict();

export type PaywallHandoffData = z.infer<typeof paywallHandoffSchema>;

/**
 * The primary CTA is rendered by the orchestrator's global footer
 * (`STEP_TYPES_WITH_GLOBAL_FOOTER` includes `paywall_handoff`), which
 * calls `handoffRedirect` for terminal steps. This renderer only shows
 * the hero content — headline, subline, and bullet points.
 *
 * The optional "Maybe later" secondary CTA fires a `defer` action for
 * analytics; it stays in the renderer since it's step-specific.
 */
const PaywallHandoffRenderer = ({ data, step, onAnswer }: RendererProps<PaywallHandoffData>) => (
  <section
    data-testid={`renderer-paywall-handoff-${step.id}`}
    className="flex flex-col items-center gap-6 py-6"
  >
    <h1
      className="max-w-md text-center text-2xl font-semibold leading-tight"
      style={{ color: "hsl(var(--brand-bg-foreground))" }}
    >
      {data.headline}
    </h1>

    {data.subline && (
      <p className="max-w-md text-center text-sm" style={{ color: "hsl(var(--hero-fg-muted))" }}>
        {data.subline}
      </p>
    )}

    {data.bullets && data.bullets.length > 0 && (
      <ul className="flex w-full max-w-sm flex-col gap-2">
        {data.bullets.map((bullet, bulletIdx) => (
          <li
            key={`${bulletIdx}-${bullet}`}
            className="flex items-start gap-3 rounded-xl p-3 text-sm"
            style={{
              background: "hsl(var(--hero-success) / 0.06)",
              color: "hsl(var(--brand-bg-foreground))",
            }}
          >
            <span
              className="mt-0.5 inline-flex shrink-0"
              style={{ color: "hsl(var(--hero-success))" }}
            >
              <Icon icon="gravity-ui:check" width={18} height={18} />
            </span>
            <span className="flex-1">{bullet}</span>
          </li>
        ))}
      </ul>
    )}

    {data.secondaryLabel && (
      <Button
        variant="ghost"
        size="sm"
        onPress={() =>
          onAnswer(data.answerKey, {
            value: null,
            action: ACTIONS.defer,
          })
        }
        className="opacity-70"
      >
        {data.secondaryLabel}
      </Button>
    )}
  </section>
);

export const paywallHandoffRenderer = createRenderer({
  type: "paywall_handoff",
  dataSchema: paywallHandoffSchema,
  label: "Paywall handoff",
  Component: PaywallHandoffRenderer,
});

export default paywallHandoffRenderer;
